import { useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PlayerChip, SelectField } from '../ui';
import { relOf } from './liveFormat';

/**
 * EventFeed — expandable rows, times relative to the first event, type filter, auto-scroll that
 * pauses when the user scrolls up (with a "jump to latest" pill counting missed events).
 * Events: session:event { key, value, ts } (+ slot once the multiplayer relay adds it).
 */
export default function EventFeed({ events, height = 480, fill = false, nameOfSlot, style }) {
    const listRef = useRef(null);
    const programmatic = useRef(false);
    const [type, setType] = useState('all');
    const [player, setPlayer] = useState('all');
    const [open, setOpen] = useState({});
    const [follow, setFollow] = useState(true);
    const [missedFrom, setMissedFrom] = useState(0);

    const hasSlots = events.some((e) => e.slot != null);
    const types = useMemo(() => [...new Set(events.map((e) => e.key).filter(Boolean))].sort(), [events]);
    const slots = useMemo(() => [...new Set(events.map((e) => e.slot).filter((s) => s != null && s > 0))].sort(), [events]);
    const shown = events.filter((e) => (type === 'all' || e.key === type) && (player === 'all' || String(e.slot ?? 0) === player));
    const t0 = events.length ? events[0].ts || events[0]._localTs : 0;

    useEffect(() => {
        const el = listRef.current;
        if (el && follow) {
            programmatic.current = true;
            el.scrollTop = el.scrollHeight;
        }
    }, [shown.length, follow]);

    const onScroll = (e) => {
        if (programmatic.current) { programmatic.current = false; return; }
        const el = e.currentTarget;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        if (atBottom !== follow) {
            setFollow(atBottom);
            setMissedFrom(atBottom ? 0 : events.length);
        }
    };
    const missed = follow ? 0 : Math.max(0, events.length - missedFrom);
    const fmtValue = (v) => (typeof v === 'string' ? v : JSON.stringify(v));

    return (
        <Panel
            label="Event feed"
            className="ls-feed"
            style={style}
            title="Event feed"
            right={(
                <>
                    {hasSlots ? (
                        <SelectField small ariaLabel="Filter by player" value={player} onChange={(v) => { setPlayer(v); setFollow(true); }} style={{ width: 132 }}
                            options={[{ value: 'all', label: 'All players' }, ...slots.map((s) => ({ value: String(s), label: 'P' + s + (nameOfSlot?.(s) ? ' · ' + nameOfSlot(s).split(' ')[0] : '') })), { value: '0', label: 'Game' }]} />
                    ) : null}
                    <SelectField small ariaLabel="Filter by event type" value={type} onChange={(v) => { setType(v); setFollow(true); }} style={{ width: 150 }}
                        options={[{ value: 'all', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))]} />
                </>
            )}
            footer={<span className="ls-caption" title="The game sends at most one event every 0.25–0.3 s, so the live feed can skip events. The recorded session has them all.">{events.length} events · times relative to first{follow ? ' · following' : ''} · live feed may skip events</span>}
        >
            {/* fill: the panel stretches to the grid rows it spans and the list scrolls inside it */}
            <div style={fill ? { position: 'relative', flex: '1 1 auto', minHeight: 360 } : { position: 'relative' }}>
                <div ref={listRef} className="ls-feed__list" role="log" aria-live="polite" style={fill ? { position: 'absolute', inset: 0 } : { height }} onScroll={onScroll}>
                    {shown.map((e) => {
                        const isOpen = !!open[e._id];
                        const cols = hasSlots ? '64px 52px minmax(0,1fr) 12px' : '72px minmax(0,1fr) 12px';
                        return (
                            <div key={e._id} className="ls-feed__row">
                                <button type="button" className="ls-feed__btn" style={{ gridTemplateColumns: cols }} aria-expanded={isOpen} onClick={() => setOpen((o) => ({ ...o, [e._id]: !o[e._id] }))}>
                                    <span className="ls-feed__rel">{relOf((e.ts || e._localTs) - t0)}</span>
                                    {hasSlots ? (
                                        <span style={{ display: 'flex' }}>
                                            {e.slot ? <PlayerChip variant="tag" slot={e.slot} name={nameOfSlot?.(e.slot)} /> : <span className="ls-feed__gametag">Game</span>}
                                        </span>
                                    ) : null}
                                    <span className="ls-feed__main">
                                        <span className="ls-feed__type">{e.key || 'Event'}</span>
                                        <span className="ls-feed__sum">{fmtValue(e.value) || '—'}</span>
                                    </span>
                                    <span className={'eg-chevron eg-chevron--sm' + (isOpen ? ' eg-chevron--open' : '')} />
                                </button>
                                {isOpen ? (
                                    <pre className="ls-feed__pre">{JSON.stringify({ key: e.key, value: e.value, ts: e.ts, ...(e.slot != null ? { slot: e.slot } : {}), at: e.ts ? new Date(e.ts).toISOString() : undefined }, null, 2)}</pre>
                                ) : null}
                            </div>
                        );
                    })}
                    {!shown.length ? (
                        <div className="ls-feed__empty">{events.length ? 'No events match these filters' : 'No events yet'}</div>
                    ) : null}
                </div>
                {!follow ? (
                    <button type="button" className="ls-feed__pill" onClick={() => { setFollow(true); setMissedFrom(0); }}>
                        {missed ? `${missed} new · jump to latest` : 'Auto-scroll paused · jump to latest'}
                    </button>
                ) : null}
            </div>
        </Panel>
    );
}
